import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { DateTime } from "luxon";

import RegionEndpoints from "../endpoints/RegionEndpoints";
import ClientEndpoints from "../endpoints/ClientEndpoints";
import IdentityEndpoints from "../endpoints/IdentityEndpoints";
import EventEndpoints from "../endpoints/EventEndpoints";

import { WorkRegion } from "../model/WorkRegion";
import { SimpleClient } from "../model/Client";
import { SimpleIdentity } from "../model/Identity";
import { SimpleEvent } from "../model/Event";

import { LoadingManager } from "../context";

interface ApplicationState {
    // Private state (previously observable)
    waitingFor: number;
    _regions: WorkRegion[];
    _clients: SimpleClient[];
    _identities: SimpleIdentity[];
    _pendingIdentities: SimpleIdentity[];
    _events: SimpleEvent[];
    iteration: number;

    // LoadingManager interface
    startLoading: () => void;
    doneLoading: () => void;

    // Actions
    clearAll: () => void;
    refreshRegions: () => Promise<WorkRegion[]>;
    refreshClients: () => Promise<SimpleClient[]>;
    refreshEvents: () => Promise<SimpleEvent[]>;
    refreshIdentities: (
        clientId?: number,
        limit?: number,
        startAt?: number,
        search?: string,
        includeArchived?: boolean
    ) => Promise<SimpleIdentity[]>;
    refreshPendingIdentities: (clientId?: number) => Promise<SimpleIdentity[]>;
    deleteRegion: (region: WorkRegion) => Promise<void>;
    discardEvent: (event: SimpleEvent) => Promise<void>;

    // Computed properties (getters)
    regions: WorkRegion[];
    clients: SimpleClient[];
    activeClients: SimpleClient[];
    activeClientsWithBadgesEnabled: SimpleClient[];
    identities: SimpleIdentity[];
    pendingIdentities: SimpleIdentity[];
    activeIdentities: SimpleIdentity[];
    events: SimpleEvent[];
    currentEvents: SimpleEvent[];
    loading: boolean;
}

// Create store that implements LoadingManager interface
export const useApplicationStore = create<ApplicationState>()(
    devtools(
        (set, get) => ({
            // Initial state
            waitingFor: 0,
            _regions: [],
            _clients: [],
            _identities: [],
            _pendingIdentities: [],
            _events: [],
            iteration: 1,

            // LoadingManager implementation
            startLoading: () => {
                set((state) => ({
                    waitingFor: state.waitingFor + 1
                }));
            },

            doneLoading: () => {
                set((state) => ({
                    waitingFor: state.waitingFor - 1 <= 0 ? 0 : state.waitingFor - 1
                }));
            },

            // Actions
            clearAll: () => {
                set({
                    _regions: [],
                    _clients: [],
                    _identities: [],
                    _events: [],
                    waitingFor: 0
                });
            },

            refreshRegions: async () => {
                const { startLoading, doneLoading } = get();
                startLoading();
                try {
                    const regions = await RegionEndpoints.getRegionList();
                    set({ _regions: regions });
                    doneLoading();
                    return regions;
                } catch (err) {
                    console.error(err);
                    doneLoading();
                    throw err;
                }
            },

            refreshClients: async () => {
                const { startLoading, doneLoading } = get();
                startLoading();
                try {
                    const clients = await ClientEndpoints.getClientList();
                    set({ _clients: clients });
                    doneLoading();
                    return clients;
                } catch (err) {
                    console.error(err);
                    doneLoading();
                    throw err;
                }
            },

            refreshEvents: async () => {
                const { startLoading, doneLoading } = get();
                startLoading();
                try {
                    const events = await EventEndpoints.getEventList();
                    set({ _events: events });
                    doneLoading();
                    return events;
                } catch (err) {
                    console.error(err);
                    doneLoading();
                    throw err;
                }
            },

            refreshIdentities: async (
                clientId: number = undefined,
                limit: number = undefined,
                startAt: number = undefined,
                search: string = undefined,
                includeArchived: boolean = false
            ) => {
                const { startLoading, doneLoading } = get();
                startLoading();
                const currentIteration = get().iteration + 1;
                set({ iteration: currentIteration });

                try {
                    const identities = await IdentityEndpoints.getIdentityList(
                        clientId,
                        undefined,
                        undefined,
                        limit,
                        startAt,
                        search,
                        includeArchived
                    );
                    
                    // Only update if this is still the current iteration
                    if (currentIteration === get().iteration) {
                        set({ _identities: identities });
                    }
                    
                    doneLoading();
                    return identities;
                } catch (err) {
                    console.error(err);
                    doneLoading();
                    throw err;
                }
            },

            refreshPendingIdentities: async (clientId?: number) => {
                const { startLoading, doneLoading } = get();
                startLoading();
                try {
                    const identities = await IdentityEndpoints.getPendingIdentityList(clientId);
                    set({ _pendingIdentities: identities });
                    doneLoading();
                    return identities;
                } catch (err) {
                    console.error(err);
                    doneLoading();
                    throw err;
                }
            },

            deleteRegion: async (region: WorkRegion) => {
                const { startLoading, doneLoading, refreshRegions } = get();
                startLoading();
                try {
                    await RegionEndpoints.deleteRegion(region.id);
                    await refreshRegions();
                    doneLoading();
                } catch (err) {
                    doneLoading();
                    throw err;
                }
            },

            discardEvent: async (event: SimpleEvent) => {
                const { startLoading, doneLoading, refreshEvents } = get();
                startLoading();
                try {
                    await EventEndpoints.discardEvent(event.id);
                    await refreshEvents();
                    doneLoading();
                } catch (err) {
                    doneLoading();
                    throw err;
                }
            },

            // Computed properties as getters
            get regions() {
                return get()._regions.slice().sort((a, b) => (a.name > b.name ? 1 : -1));
            },

            get clients() {
                return get()._clients.slice().sort((a, b) => (a.name > b.name ? 1 : -1));
            },

            get activeClients() {
                return get().clients.filter((x) => !x.dateArchived);
            },

            get activeClientsWithBadgesEnabled() {
                return get().clients.filter(
                    (x) => x.clientBadgesEnabled && !x.dateArchived
                );
            },

            get identities() {
                return get()._identities
                    .slice()
                    .sort((a, b) => (a.lastName > b.lastName ? 1 : -1));
            },

            get pendingIdentities() {
                return get()._pendingIdentities
                    .slice()
                    .sort((a, b) => (a.lastName > b.lastName ? 1 : -1));
            },

            get activeIdentities() {
                return get().identities.filter((x) => !x.dateArchived);
            },

            get events() {
                return get()._events
                    .slice()
                    .sort((a, b) => (a.startDate < b.startDate ? 1 : -1));
            },

            get currentEvents() {
                return get()._events
                    .slice()
                    .filter((e) => DateTime.fromJSDate(e.endDate) > DateTime.now())
                    .sort((a, b) => (a.startDate < b.startDate ? 1 : -1));
            },

            get loading() {
                return get().waitingFor > 0;
            }
        }),
        {
            name: 'application-store',
        }
    )
);

// Create a class-like interface that matches the original MobX ApplicationStore
// This allows existing code to continue working with the same API
export class ApplicationStore implements LoadingManager {
    private store = useApplicationStore;

    startLoading() {
        this.store.getState().startLoading();
    }

    doneLoading() {
        this.store.getState().doneLoading();
    }

    clearAll() {
        this.store.getState().clearAll();
    }

    refreshRegions(): Promise<WorkRegion[]> {
        return this.store.getState().refreshRegions();
    }

    refreshClients(): Promise<SimpleClient[]> {
        return this.store.getState().refreshClients();
    }

    refreshEvents(): Promise<SimpleEvent[]> {
        return this.store.getState().refreshEvents();
    }

    refreshIdentities(
        clientId: number = undefined,
        limit: number = undefined,
        startAt: number = undefined,
        search: string = undefined,
        includeArchived: boolean = false
    ): Promise<SimpleIdentity[]> {
        return this.store.getState().refreshIdentities(
            clientId,
            limit,
            startAt,
            search,
            includeArchived
        );
    }

    refreshPendingIdentities(clientId?: number): Promise<SimpleIdentity[]> {
        return this.store.getState().refreshPendingIdentities(clientId);
    }

    deleteRegion(region: WorkRegion): Promise<void> {
        return this.store.getState().deleteRegion(region);
    }

    discardEvent(event: SimpleEvent): Promise<void> {
        return this.store.getState().discardEvent(event);
    }

    get regions(): WorkRegion[] {
        return this.store.getState().regions;
    }

    get clients(): SimpleClient[] {
        return this.store.getState().clients;
    }

    get activeClients(): SimpleClient[] {
        return this.store.getState().activeClients;
    }

    get activeClientsWithBadgesEnabled(): SimpleClient[] {
        return this.store.getState().activeClientsWithBadgesEnabled;
    }

    get identities(): SimpleIdentity[] {
        return this.store.getState().identities;
    }

    get pendingIdentities(): SimpleIdentity[] {
        return this.store.getState().pendingIdentities;
    }

    get activeIdentities(): SimpleIdentity[] {
        return this.store.getState().activeIdentities;
    }

    get events(): SimpleEvent[] {
        return this.store.getState().events;
    }

    get currentEvents(): SimpleEvent[] {
        return this.store.getState().currentEvents;
    }

    get loading(): boolean {
        return this.store.getState().loading;
    }
}